import axios from 'axios';

// Use environment variable for API base URL, fallback to deployed backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ============================================================================
// CHAT & MEMORY API
// ============================================================================

export const sendChatCommand = (command, sessionId = 'default') => {
  return axios.post(`${API_BASE_URL}/chat-command`, { 
    command,
    session_id: sessionId
  });
};

export const clearConversationMemory = (sessionId = 'default') => {
  return axios.post(`${API_BASE_URL}/clear-memory`, { 
    session_id: sessionId
  });
};

export const getNewSession = () => {
  return axios.get(`${API_BASE_URL}/new-session`);
};

// ============================================================================
// ALERT SYSTEM API
// ============================================================================

export const createAlert = (symbol, condition, targetPrice, message = '') => {
  return axios.post(`${API_BASE_URL}/create-alert`, {
    symbol,
    condition,
    target_price: targetPrice,
    message
  });
};

export const getAlerts = (symbol = null) => {
  const params = symbol ? { symbol } : {};
  return axios.get(`${API_BASE_URL}/alerts`, { params });
};

export const deleteAlert = (alertId) => {
  return axios.delete(`${API_BASE_URL}/delete-alert`, {
    data: { alert_id: alertId }
  });
};

export const getAlertSummary = () => {
  return axios.get(`${API_BASE_URL}/alert-summary`);
};

// ============================================================================
// ALGO TRADING API
// ============================================================================

export const getAlgoStatus = () => {
  return axios.get(`${API_BASE_URL}/algo/status`);
};

export const startAlgo = (payload) => {
  return axios.post(`${API_BASE_URL}/algo/start`, payload);
};

export const stopAlgo = () => {
  return axios.post(`${API_BASE_URL}/algo/stop`);
};

// ============================================================================
// VOICE ALERTS API
// ============================================================================

export const createVoiceAlert = (alertData) => {
  return axios.post(`${API_BASE_URL}/voice-alerts/create`, alertData);
};

export const getVoiceAlerts = (userId = null, symbol = null) => {
  const params = {};
  if (userId) params.user_id = userId;
  if (symbol) params.symbol = symbol;
  return axios.get(`${API_BASE_URL}/voice-alerts/list`, { params });
};

export const deleteVoiceAlert = (alertId) => {
  return axios.delete(`${API_BASE_URL}/voice-alerts/${alertId}`);
};

export const getVoiceAlertHistory = (userId = null, limit = 50) => {
  const params = { limit };
  if (userId) params.user_id = userId;
  return axios.get(`${API_BASE_URL}/voice-alerts/history`, { params });
};

export const createVoicePreview = (text, voiceConfig) => {
  return axios.post(`${API_BASE_URL}/voice-alerts/voice/preview`, {
    text,
    voice_config: voiceConfig
  });
};

export const getAvailableVoices = (language = 'en-US') => {
  return axios.get(`${API_BASE_URL}/voice-alerts/voice/available`, {
    params: { language }
  });
};

export const testVoiceAlert = (text, voiceConfig) => {
  return axios.post(`${API_BASE_URL}/voice-alerts/voice/test`, {
    text,
    voice_config: voiceConfig
  });
};

export const getVoiceAlertStatus = () => {
  return axios.get(`${API_BASE_URL}/voice-alerts/status`);
};

// ============================================================================
// AI AGENTS API
// ============================================================================

export const getAgentsStatus = () => {
  return axios.get(`${API_BASE_URL}/agents/status`);
};

export const startParameterOptimization = (optimizationInterval = 300) => {
  return axios.post(`${API_BASE_URL}/agents/parameter-optimization/start`, {
    optimization_interval: optimizationInterval
  });
};

export const stopParameterOptimization = () => {
  return axios.post(`${API_BASE_URL}/agents/parameter-optimization/stop`);
};

export const getParameterRecommendations = () => {
  return axios.get(`${API_BASE_URL}/agents/parameter-optimization/recommendations`);
};

export const getOptimizationHistory = (limit = 10) => {
  return axios.get(`${API_BASE_URL}/agents/parameter-optimization/history`, {
    params: { limit }
  });
};

export const applyParameterRecommendations = () => {
  return axios.post(`${API_BASE_URL}/agents/parameter-optimization/apply`);
};

export const getIntelligenceSummary = () => {
  return axios.get(`${API_BASE_URL}/agents/intelligence/summary`);
};

export const triggerImmediateOptimization = () => {
  return axios.post(`${API_BASE_URL}/agents/intelligence/optimize-now`);
};

// ============================================================================
// NUMEROLOGY TRADING API
// ============================================================================

export const getNumerologyTradingSignals = (userId, symbols = []) => {
  return axios.post(`${API_BASE_URL}/numerology/trading-signals`, {
    user_id: userId,
    symbols: symbols
  });
};

export const getNumerologyPortfolioAnalysis = (userId) => {
  return axios.get(`${API_BASE_URL}/numerology/portfolio-analysis/${userId}`);
};

export const getBuySignalsOnly = (userId, batchSize = 50, minConfidence = 0.6) => {
  return axios.post(`${API_BASE_URL}/numerology/bulk-analysis-buy-only/${userId}`, {
    batch_size: batchSize,
    min_confidence: minConfidence
  });
};

export const getTopSymbols = (count = 50) => {
  return axios.get(`${API_BASE_URL}/numerology/top-symbols`, {
    params: { count }
  });
};

export const getNumerologyHealth = () => {
  return axios.get(`${API_BASE_URL}/numerology/health`);
};

// ============================================================================
// AUTHENTICATION API
// ============================================================================

export const login = (email, password) => {
  return axios.post(`${API_BASE_URL}/auth/login`, {
    email,
    password
  });
};

export const signup = (userData) => {
  return axios.post(`${API_BASE_URL}/auth/signup`, userData);
};

export const getProfile = (userId) => {
  return axios.get(`${API_BASE_URL}/auth/profile/${userId}`);
};

export const updateProfile = (userId, userData) => {
  return axios.put(`${API_BASE_URL}/auth/profile/${userId}`, userData);
};

// ============================================================================
// ASTRO INTELLIGENCE API
// ============================================================================

export const getAstroIntelligence = (userId, symbol = null) => {
  const params = { user_id: userId };
  if (symbol) params.symbol = symbol;
  return axios.get(`${API_BASE_URL}/astro-intelligence/analysis`, { params });
};

export const getAstroIntelligenceHealth = () => {
  return axios.get(`${API_BASE_URL}/astro-intelligence/health`);
};

// ============================================================================
// FORECAST API
// ============================================================================

export const getForecast = (symbol, days = 30, dataSource = null) => {
  const params = { symbol, days };
  if (dataSource) params.data_source = dataSource;
  return axios.get(`${API_BASE_URL}/forecast/fetch`, { params });
};

export const getForecastHealth = () => {
  return axios.get(`${API_BASE_URL}/forecast/health`);
};

// ============================================================================
// GROUPS API
// ============================================================================

export const getGroups = () => {
  return axios.get(`${API_BASE_URL}/groups/list`);
};

export const createGroup = (groupData) => {
  return axios.post(`${API_BASE_URL}/groups/create`, groupData);
};

export const updateGroup = (groupId, groupData) => {
  return axios.put(`${API_BASE_URL}/groups/${groupId}`, groupData);
};

export const deleteGroup = (groupId) => {
  return axios.delete(`${API_BASE_URL}/groups/${groupId}`);
};

export const getGroupsHealth = () => {
  return axios.get(`${API_BASE_URL}/groups/health`);
};

// ============================================================================
// SYMBOLS API
// ============================================================================

export const getSymbols = (query = null, limit = 100) => {
  const params = { limit };
  if (query) params.query = query;
  return axios.get(`${API_BASE_URL}/symbols/search`, { params });
};

export const getSymbolsHealth = () => {
  return axios.get(`${API_BASE_URL}/symbols/health`);
};

// ============================================================================
// SYSTEM HEALTH API
// ============================================================================

export const getSystemHealth = () => {
  return axios.get(`${API_BASE_URL}/health`);
};

export const getSystemStatus = () => {
  return axios.get(`${API_BASE_URL}/status`);
};

// ============================================================================
// WEBSOCKET CONNECTIONS
// ============================================================================

export const createVoiceAlertWebSocket = (userId) => {
  const wsUrl = API_BASE_URL.replace('http', 'ws');
  return new WebSocket(`${wsUrl}/voice-alerts/ws/${userId}`);
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.detail || error.response.data?.message || 'An error occurred',
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // Request was made but no response received
    return {
      message: 'Network error - please check your connection',
      status: 0,
      data: null
    };
  } else {
    // Something else happened
    return {
      message: error.message || 'An unexpected error occurred',
      status: -1,
      data: null
    };
  }
};

export const isApiHealthy = async () => {
  try {
    const response = await getSystemHealth();
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Chat & Memory
  sendChatCommand,
  clearConversationMemory,
  getNewSession,
  
  // Alerts
  createAlert,
  getAlerts,
  deleteAlert,
  getAlertSummary,
  
  // Algo Trading
  getAlgoStatus,
  startAlgo,
  stopAlgo,
  
  // Voice Alerts
  createVoiceAlert,
  getVoiceAlerts,
  deleteVoiceAlert,
  getVoiceAlertHistory,
  createVoicePreview,
  getAvailableVoices,
  testVoiceAlert,
  getVoiceAlertStatus,
  
  // AI Agents
  getAgentsStatus,
  startParameterOptimization,
  stopParameterOptimization,
  getParameterRecommendations,
  getOptimizationHistory,
  applyParameterRecommendations,
  getIntelligenceSummary,
  triggerImmediateOptimization,
  
  // Numerology
  getNumerologyTradingSignals,
  getNumerologyPortfolioAnalysis,
  getBuySignalsOnly,
  getTopSymbols,
  getNumerologyHealth,
  
  // Authentication
  login,
  signup,
  getProfile,
  updateProfile,
  
  // Other APIs
  getAstroIntelligence,
  getAstroIntelligenceHealth,
  getForecast,
  getForecastHealth,
  getGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroupsHealth,
  getSymbols,
  getSymbolsHealth,
  getSystemHealth,
  getSystemStatus,
  
  // WebSocket
  createVoiceAlertWebSocket,
  
  // Utilities
  handleApiError,
  isApiHealthy
};